import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import SEOHead from "@/components/feature/SEOHead";

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    content: `By accessing or using The Wraith Chronicles website ("the Site"), you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, please do not use the Site. We reserve the right to update these terms at any time, and continued use of the Site constitutes acceptance of any changes.`,
  },
  {
    id: "use-of-site",
    title: "Use of the Site",
    items: [
      {
        label: "Permitted Use",
        text: `You may access and use the Site for personal, non-commercial purposes — reading the story, exploring lore, viewing illustrations, and engaging with the content as a reader.`,
      },
      {
        label: "Prohibited Use",
        text: `You may not use the Site to scrape, harvest, or collect data; to distribute spam or unsolicited messages; to attempt to gain unauthorized access to any part of the Site; or for any unlawful purpose.`,
      },
      {
        label: "Age Requirement",
        text: `This Site contains mature themes including violence and survival horror. By using the Site, you confirm that you are at least 13 years of age. Content may not be appropriate for younger readers.`,
      },
    ],
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    content: `All content on this Site — including but not limited to the story text, character names, illustrations, artwork, logos, and design — is the exclusive property of the author and is protected by copyright law. You may not reproduce, distribute, modify, or create derivative works from any content on this Site without express written permission.`,
  },
  {
    id: "fan-content",
    title: "Fan Content",
    items: [
      {
        label: "What's Welcome",
        text: `We love fan art, fan fiction, and creative engagement with the world of The Wraith Chronicles. Non-commercial fan works that clearly credit the source are welcomed and celebrated.`,
      },
      {
        label: "What's Not Permitted",
        text: `Selling fan-made merchandise, using characters or story elements for commercial gain, or presenting fan content as official material is not permitted without written authorization.`,
      },
    ],
  },
  {
    id: "user-submissions",
    title: "User Submissions",
    content: `When you submit a message, comment, or any content through forms on this Site, you grant us a non-exclusive, royalty-free license to use, display, and share that content in connection with the Site and its promotion (e.g., sharing reader reactions). You retain ownership of your submission. We will never sell your words or use them out of context.`,
  },
  {
    id: "third-party-links",
    title: "Third-Party Links",
    content: `The Site may contain links to external websites such as social media platforms. These links are provided for convenience only. We have no control over the content or practices of third-party sites and accept no responsibility for them. Visiting external links is at your own risk.`,
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    items: [
      {
        label: "Fiction",
        text: `The Wraith Chronicles is a work of fiction. All characters, events, locations, and organizations are entirely fictional. Any resemblance to real persons, living or dead, is purely coincidental.`,
      },
      {
        label: "No Warranty",
        text: `The Site is provided "as is" without warranties of any kind. We do not guarantee that the Site will be error-free, uninterrupted, or free of harmful components.`,
      },
    ],
  },
  {
    id: "limitation-of-liability",
    title: "Limitation of Liability",
    content: `To the fullest extent permitted by law, The Wraith Chronicles and its author shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Site or its content. Our total liability for any claim shall not exceed the amount you paid to access the Site (which, since the Site is free, is zero).`,
  },
  {
    id: "governing-law",
    title: "Governing Law",
    content: `These Terms of Use shall be governed by and construed in accordance with applicable law. Any disputes arising from these terms or your use of the Site shall be resolved through good-faith negotiation before any formal legal proceedings.`,
  },
  {
    id: "contact",
    title: "Contact",
    content: `If you have questions about these Terms of Use, please reach out through the contact form on this Site. We're real people and we'll actually read it.`,
  },
];

export default function TermsOfUsePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen w-full">
      <SEOHead
        title="Terms of Use | The Wraith Chronicles"
        description="Terms of Use for The Wraith Chronicles website. Read the rules governing your use of this site and its content."
        noIndex
      />
      <Navbar />

      {/* Hero */}
      <div className="relative w-full pt-36 pb-16 px-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent, transparent 28px, #C8922A 28px, #C8922A 29px)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />

        <div className="relative max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="w-12 h-px bg-[#C8922A]/30" />
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-file-text-line text-[#C8922A]/50 text-base" />
            </div>
            <div className="w-12 h-px bg-[#C8922A]/30" />
          </div>

          <h1 className="font-['Special_Elite'] text-white text-5xl md:text-6xl tracking-[0.06em] uppercase mb-6 leading-tight">
            Terms of<br />
            <span className="text-[#C8922A]">Use</span>
          </h1>

          <p className="font-['Share_Tech_Mono'] text-[#6A6A6A] text-xs tracking-[0.25em] uppercase">
            Effective Date: April 2026
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-2xl mx-auto px-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-[#1E1E1E]" />
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-scales-line text-[#C8922A]/30 text-xs" />
          </div>
          <div className="flex-1 h-px bg-[#1E1E1E]" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-16 space-y-12">
        {sections.map((section) => (
          <div key={section.id} id={section.id}>
            <h4 className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.3em] uppercase mb-5 flex items-center gap-3">
              <a href={`#${section.id}`} className="hover:text-[#D9A030] transition-colors">
                {section.title}
              </a>
              <span className="flex-1 h-px bg-[#1A1A1A]" />
            </h4>

            {section.content && (
              <p className="font-['Rajdhani'] text-[#888] text-base leading-relaxed">
                {section.content}
              </p>
            )}

            {section.items && (
              <ul className="space-y-5">
                {section.items.map((item) => (
                  <li key={item.label} className="border-l-2 border-[#1E1E1E] pl-5">
                    <div className="font-['Share_Tech_Mono'] text-[#555] text-xs tracking-[0.2em] uppercase mb-1">
                      {item.label}
                    </div>
                    <p className="font-['Rajdhani'] text-[#888] text-base leading-relaxed">
                      {item.text}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Cross-link to Privacy Policy */}
      <div className="max-w-2xl mx-auto px-6 pb-8">
        <div className="border border-[#1A1A1A] bg-[#0D0D0D] px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-[0.25em] uppercase mb-1">
              Also Read
            </div>
            <p className="font-['Rajdhani'] text-[#666] text-sm">
              Our Privacy Policy explains how we handle your personal data.
            </p>
          </div>
          <button
            onClick={() => navigate("/privacy-policy")}
            className="flex items-center gap-2 border border-[#C8922A]/30 hover:border-[#C8922A]/60 text-[#C8922A]/70 hover:text-[#C8922A] text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-5 py-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shield-line text-sm" />
            Privacy Policy
          </button>
        </div>
      </div>

      {/* Back link */}
      <div className="max-w-2xl mx-auto px-6 pb-20 text-center">
        <button
          onClick={() => navigate("/")}
          className="font-['Share_Tech_Mono'] text-[#333] hover:text-[#C8922A]/60 text-xs tracking-[0.25em] uppercase transition-colors duration-300 cursor-pointer flex items-center gap-2 mx-auto"
        >
          <i className="ri-arrow-left-line text-xs" />
          Back to Home
        </button>
      </div>

      <Footer />
    </div>
  );
}
